/**
 * ADMIN MODULE — Modules
 * The launch switch for every product module: build it, then decide when
 * customers see it. Grouped into the same Sales / Marketing / Operations
 * columns the customer launcher uses.
 *
 * This is NOT billing. Two independent gates, and a module needs both:
 *   • is_live here        — "have we finished building it?"   (staff)
 *   • entitled_modules    — "has this tenant paid for it?"    (plan, RLS)
 * Flipping something live does not grant it to anyone who isn't on a plan
 * that includes it.
 */

import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout.jsx";
import {
  CATEGORIES,
  listModules,
  setModuleLive,
  setModuleCategory,
} from "../utils/moduleCatalogService.js";

function fmtDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminModulesView() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    listModules()
      .then(setModules)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const out = CATEGORIES.map((c) => ({
      ...c,
      items: modules.filter((m) => m.category === c.key),
    }));
    const orphans = modules.filter((m) => !CATEGORIES.some((c) => c.key === m.category));
    if (orphans.length) out.push({ key: null, label: "Uncategorised", items: orphans });
    return out;
  }, [modules]);

  const liveCount = modules.filter((m) => m.is_live).length;

  const apply = (updated) =>
    setModules((rows) => rows.map((r) => (r.module_id === updated.module_id ? updated : r)));

  const toggle = async (m) => {
    setBusy(m.module_id);
    setErr(""); setNote("");
    try {
      const updated = await setModuleLive(m.module_id, !m.is_live, !!m.went_live_at);
      apply(updated);
      setNote(`${updated.name} is now ${updated.is_live ? "live for customers" : "hidden from customers"}.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  };

  const move = async (m, category) => {
    setBusy(m.module_id);
    setErr(""); setNote("");
    try {
      apply(await setModuleCategory(m.module_id, category || null));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminLayout>
      <section className="settings-card wide">
        <h2 style={{ marginTop: 0 }}>Modules</h2>
        <p className="muted">
          Decide when a finished module goes live for customers. A module that
          isn’t live still appears on their launcher as a <strong>Soon</strong> tile —
          it just has no link, and the route turns anyone away who types the URL.
        </p>
        <p className="muted">
          This is separate from billing: a customer also needs a plan that includes
          the module. Turning something live here doesn’t give it to anyone who
          hasn’t paid for it.
        </p>

        {err && <p className="error">{err}</p>}
        {note && <p className="success">{note}</p>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <p className="muted">
              <strong>{liveCount}</strong> of {modules.length} modules live.
            </p>

            {grouped.map((group) => (
              <div key={group.label} style={{ marginTop: 24 }}>
                <h3 style={{ margin: "0 0 8px" }}>{group.label}</h3>
                {group.items.length === 0 ? (
                  <p className="muted">Nothing in this group.</p>
                ) : (
                  <table className="admin-table" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Route</th>
                        <th>Group</th>
                        <th>Live since</th>
                        <th style={{ textAlign: "right" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((m) => (
                        <tr key={m.module_id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{m.name}</div>
                            {m.description && <div className="muted" style={{ fontSize: 13 }}>{m.description}</div>}
                          </td>
                          <td className="muted" style={{ fontSize: 13 }}>
                            {m.route || <em>no screen yet</em>}
                          </td>
                          <td>
                            <select
                              value={m.category || ""}
                              disabled={busy === m.module_id}
                              onChange={(e) => move(m, e.target.value)}
                              style={{ width: "auto" }}
                            >
                              <option value="">—</option>
                              {CATEGORIES.map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="muted" style={{ fontSize: 13 }}>
                            {fmtDate(m.went_live_at) || "—"}
                          </td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <span
                              className={`module-status ${m.is_live ? "live" : "soon"}`}
                              style={{ marginRight: 10 }}
                            >
                              {m.is_live ? "Live" : "Soon"}
                            </span>
                            <button
                              className={m.is_live ? "btn secondary" : "btn"}
                              disabled={busy === m.module_id || !m.route}
                              title={!m.route ? "No screen built yet — nothing to launch." : undefined}
                              onClick={() => toggle(m)}
                            >
                              {busy === m.module_id
                                ? "Saving…"
                                : m.is_live ? "Take offline" : "Take live"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </>
        )}
      </section>
    </AdminLayout>
  );
}
